import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  cancelKickoffQueue,
  fetchMonitors,
  type AlertSeverity,
  type KickoffQueueRow,
  type MonitorAlert,
  type MonitorCategory,
} from "../api/client";

// Shape of detail.events[] in an ensemble_disagreement alert. Backend builds
// these in dashboard_api._check_ensemble_disagreement.
interface DisagreementEvent {
  decision_ts: string;
  ensemble_action: number;
  std_dev: number;
  contributors: { model_id: string; individual_action: number }[];
}
interface DisagreementDetail {
  count: number;
  max_std_dev: number;
  threshold: number;
  events: DisagreementEvent[];
}

// Shape of detail in a tpe_kickoff_status alert. Backend builds in
// dashboard_api._check_tpe_kickoff_status.
interface KickoffQueueDetail {
  queue_id: string;
  fire_at: string;
  total: number;
  failed_count: number;
  running_count: number;
  stalled: boolean;
  items: KickoffQueueRow[];
}

// PM-facing health page: catch silent failures (missing models, stale
// promotions, ticker rule violations) before they cost money. Page
// layout is a fixed grid — top summary, then half-width error +
// heartbeat cards, a row of four quarter-width violation/coverage
// cards, then a full-width promotion-freshness status table.

const SEVERITY_COLOR: Record<AlertSeverity, string> = {
  critical: "#ef4444",
  warning: "#fbbf24",
  info: "#64748b",
};

function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const c = SEVERITY_COLOR[severity];
  return (
    <span
      style={{
        background: `${c}22`,
        color: c,
        border: `1px solid ${c}55`,
        borderRadius: 4,
        padding: "1px 6px",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {severity}
    </span>
  );
}

function fmtTs(ts: string | null | undefined) {
  if (!ts) return "—";
  return ts.replace("T", " ").slice(0, 16);
}

function fmtDate(ts: string | null | undefined) {
  if (!ts) return "—";
  return ts.slice(0, 10);
}

function TickerLink({ ticker }: { ticker: string | null }) {
  if (!ticker) return <span style={{ color: "#94a3b8" }}>—</span>;
  return (
    <Link
      to={`/symbols/${encodeURIComponent(ticker)}`}
      style={{ color: "#e2e8f0", textDecoration: "none", fontWeight: 600 }}
    >
      {ticker}
    </Link>
  );
}

// ----- Compact rows for the quarter-width cards -----

function InsufficientModelsRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as { active_models?: number };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td className="num" style={{ color: SEVERITY_COLOR.critical, fontWeight: 600 }}>
        {d.active_models ?? "—"}/3
      </td>
    </tr>
  );
}

function NoModelsRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as { last_decision_ts?: string };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtTs(d.last_decision_ts)}</td>
    </tr>
  );
}

function SellsOnlyBoughtRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as {
    mode_changed_at?: string;
    last_decision_ts?: string;
    last_buy_ts?: string;
  };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(d.mode_changed_at)}</td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(d.last_decision_ts)}</td>
      <td style={{ color: SEVERITY_COLOR.critical, fontSize: 12, fontWeight: 600 }}>
        {fmtDate(d.last_buy_ts)}
      </td>
    </tr>
  );
}

function DisabledTradedRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as {
    mode_changed_at?: string;
    last_decision_ts?: string;
    last_trade_ts?: string;
  };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(d.mode_changed_at)}</td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtDate(d.last_decision_ts)}</td>
      <td style={{ color: SEVERITY_COLOR.critical, fontSize: 12, fontWeight: 600 }}>
        {fmtDate(d.last_trade_ts)}
      </td>
    </tr>
  );
}

// ----- Rows for the wider cards -----

function HeartbeatRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as { hours_since?: number | null; last_decision_ts?: string; trading_mode?: string };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{d.trading_mode ?? "—"}</td>
      <td className="num" style={{ color: SEVERITY_COLOR.critical }}>
        {d.hours_since == null ? "never today" : `${d.hours_since}h ago`}
      </td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtTs(d.last_decision_ts)}</td>
    </tr>
  );
}

const SIGNAL_COLOR: Record<string, string> = {
  BUY: "#22c55e",
  SELL_REDUCE: "#ef4444",
  SELL_SHORT: "#f97316",
  HOLD: "#94a3b8",
};

function LastDecisionRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as { signal_type?: string | null; last_decision_ts?: string | null };
  const sig = (d.signal_type ?? "").toUpperCase();
  const color = SIGNAL_COLOR[sig] ?? "#94a3b8";
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td style={{ color, fontWeight: 600, fontSize: 12 }}>{sig || "—"}</td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtTs(d.last_decision_ts)}</td>
    </tr>
  );
}

function DecisionErrorRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as {
    count?: number;
    sample?: { timestamp?: string; message?: string }[];
  };
  return (
    <tr>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td className="num">{d.count ?? 0}</td>
      <td style={{ color: "#94a3b8", fontSize: 11, fontFamily: "monospace" }}>
        {(d.sample?.[0]?.message ?? "—").slice(0, 80)}
      </td>
    </tr>
  );
}

function PromotionRow({ alert }: { alert: MonitorAlert }) {
  const d = alert.detail as { days_since?: number | null; last_added_at?: string; trading_mode?: string };
  return (
    <tr>
      <td><SeverityBadge severity={alert.severity} /></td>
      <td><TickerLink ticker={alert.ticker} /></td>
      <td className="num">
        {d.days_since == null ? "—" : `${d.days_since}d`}
      </td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{d.trading_mode ?? "—"}</td>
      <td style={{ color: "#94a3b8", fontSize: 12 }}>{fmtTs(d.last_added_at)}</td>
    </tr>
  );
}

// ----- Generic card scaffolding -----

interface CategoryConfig {
  headers: string[];
  numericHeaders?: Set<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowComponent: (props: { alert: MonitorAlert; key?: string }) => any;
}

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  insufficient_models: {
    headers: ["Ticker", "Active"],
    numericHeaders: new Set(["Active"]),
    rowComponent: InsufficientModelsRow,
  },
  no_models_at_all: {
    headers: ["Ticker", "Last decision"],
    rowComponent: NoModelsRow,
  },
  sells_only_bought: {
    headers: ["Ticker", "Sells-only since", "Last decision", "Last buy"],
    rowComponent: SellsOnlyBoughtRow,
  },
  disabled_traded: {
    headers: ["Ticker", "Disabled since", "Last decision", "Last trade"],
    rowComponent: DisabledTradedRow,
  },
  decision_heartbeat_stale: {
    headers: ["Ticker", "Mode", "Stale", "Last decision"],
    numericHeaders: new Set(["Stale"]),
    rowComponent: HeartbeatRow,
  },
  decision_errors_24h: {
    headers: ["Ticker", "Count", "Latest message"],
    numericHeaders: new Set(["Count"]),
    rowComponent: DecisionErrorRow,
  },
  promotion_freshness: {
    headers: ["Severity", "Ticker", "Age", "Mode", "Last promoted"],
    numericHeaders: new Set(["Age"]),
    rowComponent: PromotionRow,
  },
  last_decision_per_ticker: {
    headers: ["Ticker", "Decision", "Last Decision Time"],
    rowComponent: LastDecisionRow,
  },
};

function CategoryCard({
  category,
  flexBasis,
  treatInfoAsOk = false,
}: {
  category: MonitorCategory;
  flexBasis: string; // e.g. "calc(25% - 12px)" or "calc(50% - 8px)" or "100%"
  treatInfoAsOk?: boolean;
}) {
  const cfg = CATEGORY_CONFIG[category.id];
  if (!cfg) return null;

  const urgentCount = category.alerts.filter((a) =>
    treatInfoAsOk ? a.severity !== "info" : true,
  ).length;
  const isEmpty = category.alerts.length === 0;
  const allGood = isEmpty || (treatInfoAsOk && urgentCount === 0);

  return (
    <div className="card" style={{ flex: `1 1 ${flexBasis}`, minWidth: 260, marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{category.title}</h3>
        <div
          style={{
            fontSize: 11,
            color: allGood ? "#22c55e" : SEVERITY_COLOR.critical,
            fontWeight: 600,
          }}
        >
          {allGood ? "OK" : `${urgentCount} issue${urgentCount === 1 ? "" : "s"}`}
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
        {category.description}
      </div>
      {isEmpty ? (
        <div style={{ color: "#22c55e", fontSize: 12 }}>No alerts.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cfg.headers.map((h) => (
                  <th key={h} className={cfg.numericHeaders?.has(h) ? "num" : undefined}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {category.alerts.map((a, i) => {
                const Row = cfg.rowComponent;
                return <Row key={`${a.category}-${a.ticker ?? ""}-${a.ts}-${i}`} alert={a} />;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function findCategory(categories: MonitorCategory[], id: string): MonitorCategory | undefined {
  return categories.find((c) => c.id === id);
}

function SummaryStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: value > 0 ? color : "#475569" }}>
        {value}
      </div>
    </div>
  );
}

// ----- Ensemble-disagreement card + modal -----
//
// Doesn't use the generic CategoryCard scaffolding because it's the only
// monitor where the row payload is a full event log the user clicks
// through. Keeping the click-and-modal interaction local here avoids
// adding row-callback plumbing to every other card.

function fmtAction(n: number | undefined | null) {
  if (n == null || Number.isNaN(n)) return "—";
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(4)}`;
}

function shortModelId(id: string) {
  // model_id format: "<run_id>#<config_key>#<replicate>". Show the last
  // two segments — the run_id is identical across all ensemble members
  // and adds visual noise.
  const parts = id.split("#");
  if (parts.length < 2) return id;
  const tail = parts.slice(-2).join("#");
  // Truncate the config_key hash for readability.
  return tail.length > 24 ? `…${tail.slice(-24)}` : tail;
}

function DisagreementModal({
  alert,
  onClose,
}: {
  alert: MonitorAlert;
  onClose: () => void;
}) {
  const detail = alert.detail as unknown as DisagreementDetail;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          maxWidth: 960, width: "100%", maxHeight: "85vh", overflow: "auto",
          marginBottom: 0,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>
            {alert.ticker} — high ensemble disagreement
          </h3>
          <button onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>
          {detail.count} decision(s) today with std_dev &gt; {detail.threshold.toFixed(2)}.
          Max std_dev: {detail.max_std_dev.toFixed(3)}.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Decision time</th>
                <th>Model</th>
                <th className="num">Individual action</th>
                <th className="num">Ensemble (median)</th>
                <th className="num">Std dev</th>
                <th>Replay</th>
              </tr>
            </thead>
            <tbody>
              {detail.events.flatMap((ev) =>
                ev.contributors.map((c, idx) => {
                  // Highlight the contributor that's furthest from the median
                  // — that's the "outlier" the user is looking for.
                  const dist = Math.abs(c.individual_action - ev.ensemble_action);
                  const isOutlier =
                    dist ===
                    Math.max(
                      ...ev.contributors.map((x) => Math.abs(x.individual_action - ev.ensemble_action)),
                    );
                  return (
                    <tr key={`${ev.decision_ts}-${c.model_id}`}>
                      {idx === 0 && (
                        <td rowSpan={ev.contributors.length} style={{ fontSize: 12, color: "#94a3b8", verticalAlign: "top" }}>
                          {fmtTs(ev.decision_ts)}
                        </td>
                      )}
                      <td style={{ fontFamily: "monospace", fontSize: 11, color: isOutlier ? "#fbbf24" : "#e2e8f0" }}>
                        {shortModelId(c.model_id)} {isOutlier && "★"}
                      </td>
                      <td className="num" style={{ color: c.individual_action >= 0 ? "#22c55e" : "#ef4444" }}>
                        {fmtAction(c.individual_action)}
                      </td>
                      {idx === 0 && (
                        <td rowSpan={ev.contributors.length} className="num" style={{ verticalAlign: "top" }}>
                          {fmtAction(ev.ensemble_action)}
                        </td>
                      )}
                      {idx === 0 && (
                        <td rowSpan={ev.contributors.length} className="num" style={{ verticalAlign: "top", color: "#fbbf24" }}>
                          {ev.std_dev.toFixed(3)}
                        </td>
                      )}
                      {idx === 0 && (
                        <td rowSpan={ev.contributors.length} style={{ verticalAlign: "top" }}>
                          <Link
                            to={`/replay/${encodeURIComponent(alert.ticker ?? "")}/${encodeURIComponent(ev.decision_ts)}`}
                            onClick={onClose}
                          >
                            replay
                          </Link>
                        </td>
                      )}
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
          ★ = contributor furthest from the median in that decision. A model
          that's the outlier across many rows is a candidate to kick from the
          ensemble and retrain.
        </div>
      </div>
    </div>
  );
}

function DisagreementCard({
  category,
  onRowClick,
}: {
  category: MonitorCategory;
  onRowClick: (alert: MonitorAlert) => void;
}) {
  const isEmpty = category.alerts.length === 0;
  return (
    <div className="card" style={{ flex: "1 1 100%", marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{category.title}</h3>
        <div style={{ fontSize: 11, color: isEmpty ? "#22c55e" : SEVERITY_COLOR.warning, fontWeight: 600 }}>
          {isEmpty ? "OK" : `${category.alerts.length} ticker${category.alerts.length === 1 ? "" : "s"}`}
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
        {category.description}
      </div>
      {isEmpty ? (
        <div style={{ color: "#22c55e", fontSize: 12 }}>No alerts.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th className="num">Disagreement count</th>
                <th className="num">Max std_dev</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {category.alerts.map((a) => {
                const d = a.detail as unknown as DisagreementDetail;
                return (
                  <tr
                    key={`${a.ticker}-${a.ts}`}
                    onClick={() => onRowClick(a)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><TickerLink ticker={a.ticker} /></td>
                    <td className="num" style={{ color: SEVERITY_COLOR[a.severity], fontWeight: 600 }}>
                      {d.count}
                    </td>
                    <td className="num">{d.max_std_dev.toFixed(3)}</td>
                    <td style={{ fontSize: 11, color: "#94a3b8" }}>click to inspect →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----- TPE kickoff queue card + modal -----
//
// Same hand-rolled-card-and-modal pattern as the disagreement card
// above. Click a queue → modal with the per-position breakdown and a
// Cancel button (for queues with pending rows).

const KICKOFF_STATUS_COLOR: Record<string, string> = {
  pending: "#94a3b8",
  running: "#0ea5e9",
  done: "#22c55e",
  failed: "#ef4444",
  cancelled: "#64748b",
};

function fmtShortTs(ts: string) {
  if (!ts) return "—";
  return ts.replace("T", " ").slice(0, 16);
}

function KickoffQueueModal({
  alert,
  onClose,
  onCancelled,
}: {
  alert: MonitorAlert;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const d = alert.detail as unknown as KickoffQueueDetail;
  const [cancelling, setCancelling] = useState(false);
  const hasPending = d.items.some((it) => it.status === "pending");

  async function handleCancel() {
    if (!window.confirm(`Cancel pending positions in queue ${d.queue_id}?`)) return;
    setCancelling(true);
    try {
      const res = await cancelKickoffQueue(d.queue_id);
      window.alert(
        `Cancelled ${res.cancelled_positions.length} pending position(s). ` +
        (res.schedule_deleted ? "EventBridge schedule deleted." : "(schedule may have already fired)"),
      );
      onCancelled();
    } catch (e) {
      window.alert(`Cancel failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          maxWidth: 1100, width: "100%", maxHeight: "85vh", overflow: "auto",
          marginBottom: 0,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>
            TPE kickoff queue — <code style={{ fontSize: 14 }}>{d.queue_id}</code>
          </h3>
          <button onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>
          {d.total} config{d.total === 1 ? "" : "s"} · fire_at <code>{d.fire_at}</code>
          {d.failed_count > 0 && (
            <span style={{ color: "#ef4444", marginLeft: 8 }}>
              · {d.failed_count} failed
            </span>
          )}
          {d.running_count > 0 && (
            <span style={{ color: "#0ea5e9", marginLeft: 8 }}>
              · {d.running_count} running
            </span>
          )}
          {d.stalled && (
            <span style={{ color: "#fbbf24", marginLeft: 8 }}>
              · stalled &gt;3h
            </span>
          )}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="num">#</th>
                <th>Config</th>
                <th>Status</th>
                <th>Phase</th>
                <th>Started</th>
                <th>Finished</th>
                <th>Run / SFN</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((it) => (
                <tr key={`${it.queue_id}-${it.position}`}>
                  <td className="num">{it.position}</td>
                  <td><code>{it.config_name}</code></td>
                  <td style={{
                    color: KICKOFF_STATUS_COLOR[it.status] ?? "#cbd5e1",
                    fontWeight: 600, fontSize: 12,
                  }}>{it.status}</td>
                  <td style={{ fontSize: 12 }}>{it.phase || "—"}</td>
                  <td style={{ fontSize: 11, color: "#94a3b8" }}>{fmtShortTs(it.started_at)}</td>
                  <td style={{ fontSize: 11, color: "#94a3b8" }}>{fmtShortTs(it.finished_at)}</td>
                  <td style={{ fontSize: 11 }}>
                    {it.run_id ? (
                      <Link to={`/runs/${encodeURIComponent(it.run_id)}`}>{it.run_id}</Link>
                    ) : "—"}
                  </td>
                  <td style={{
                    fontSize: 11, color: "#fca5a5", maxWidth: 240,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }} title={it.error_msg}>
                    {it.error_msg || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasPending && (
          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              style={{
                background: cancelling ? "#334155" : "#dc2626",
                color: "#fff", border: "1px solid #b91c1c",
                padding: "6px 14px", borderRadius: 4, fontSize: 13, fontWeight: 600,
                cursor: cancelling ? "not-allowed" : "pointer",
              }}
            >{cancelling ? "Cancelling…" : "Cancel pending positions"}</button>
          </div>
        )}
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
          Cancel deletes the EventBridge schedule (if still pending) and marks any pending rows as cancelled.
          Already-running SFN executions keep running — stop them from the SFN console if needed.
        </div>
      </div>
    </div>
  );
}

function KickoffQueueCard({
  category,
  onRowClick,
}: {
  category: MonitorCategory;
  onRowClick: (alert: MonitorAlert) => void;
}) {
  const isEmpty = category.alerts.length === 0;
  return (
    <div className="card" style={{ flex: "1 1 100%", marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{category.title}</h3>
        <div style={{ fontSize: 11, color: isEmpty ? "#22c55e" : SEVERITY_COLOR.warning, fontWeight: 600 }}>
          {isEmpty ? "OK" : `${category.alerts.length} batch${category.alerts.length === 1 ? "" : "es"}`}
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
        {category.description}
      </div>
      {isEmpty ? (
        <div style={{ color: "#22c55e", fontSize: 12 }}>No alerts.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Queue</th>
                <th>Fire at (UTC)</th>
                <th className="num">Total</th>
                <th className="num">Failed</th>
                <th className="num">Running</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {category.alerts.map((a) => {
                const d = a.detail as unknown as KickoffQueueDetail;
                return (
                  <tr
                    key={`${a.category}-${d.queue_id}`}
                    onClick={() => onRowClick(a)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><code style={{ fontSize: 11 }}>{d.queue_id}</code></td>
                    <td style={{ fontSize: 12, color: "#94a3b8" }}>{fmtShortTs(d.fire_at)}</td>
                    <td className="num">{d.total}</td>
                    <td className="num" style={{
                      color: d.failed_count > 0 ? SEVERITY_COLOR.critical : "#94a3b8",
                      fontWeight: d.failed_count > 0 ? 600 : 400,
                    }}>{d.failed_count}</td>
                    <td className="num" style={{ color: d.running_count > 0 ? "#0ea5e9" : "#94a3b8" }}>
                      {d.running_count}
                    </td>
                    <td style={{ fontSize: 11, color: "#94a3b8" }}>click to inspect →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MonitorsPage() {
  const [modalAlert, setModalAlert] = useState<MonitorAlert | null>(null);
  const [kickoffModalAlert, setKickoffModalAlert] = useState<MonitorAlert | null>(null);
  const qc = useQueryClient();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["monitors"],
    queryFn: fetchMonitors,
    refetchInterval: 60 * 1000,
  });

  if (isLoading) {
    return <div className="card" style={{ color: "#94a3b8" }}>Loading monitors…</div>;
  }
  if (error) {
    return (
      <div className="card" style={{ color: "#fca5a5" }}>
        Monitors error: {String(error)}{" "}
        <button onClick={() => refetch()} style={{ marginLeft: 8 }}>Retry</button>
      </div>
    );
  }
  if (!data) return null;

  const { summary, categories, generated_at } = data;

  const errors = findCategory(categories, "decision_errors_24h");
  const heartbeat = findCategory(categories, "decision_heartbeat_stale");
  const insufficient = findCategory(categories, "insufficient_models");
  const noModels = findCategory(categories, "no_models_at_all");
  const sellsOnly = findCategory(categories, "sells_only_bought");
  const disabledTraded = findCategory(categories, "disabled_traded");
  const promotion = findCategory(categories, "promotion_freshness");
  const lastDecision = findCategory(categories, "last_decision_per_ticker");
  const disagreement = findCategory(categories, "ensemble_disagreement");
  const kickoffQueue = findCategory(categories, "tpe_kickoff_status");

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ margin: 0 }}>Monitors</h2>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            generated {fmtTs(generated_at)} {isFetching && " · refreshing…"}{" "}
            <button onClick={() => refetch()} style={{ marginLeft: 8 }}>Refresh</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
          <SummaryStat label="Critical" value={summary.critical} color={SEVERITY_COLOR.critical} />
          <SummaryStat label="Warning" value={summary.warning} color={SEVERITY_COLOR.warning} />
          <SummaryStat label="Info" value={summary.info} color={SEVERITY_COLOR.info} />
        </div>
      </div>

      {/* Top row: half-width error + heartbeat cards. These are the
          two checks a PM checks first thing in the morning. */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        {errors && <CategoryCard category={errors} flexBasis="calc(50% - 8px)" />}
        {heartbeat && <CategoryCard category={heartbeat} flexBasis="calc(50% - 8px)" />}
      </div>

      {/* Second row: four quarter-width cards. Coverage + rule
          violations. Each is a compact ticker-list per user spec. */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        {insufficient && <CategoryCard category={insufficient} flexBasis="calc(25% - 12px)" />}
        {noModels && <CategoryCard category={noModels} flexBasis="calc(25% - 12px)" />}
        {sellsOnly && <CategoryCard category={sellsOnly} flexBasis="calc(25% - 12px)" />}
        {disabledTraded && <CategoryCard category={disabledTraded} flexBasis="calc(25% - 12px)" />}
      </div>

      {/* Full-width promotion-freshness status table. Always populated
          (one row per active ticker), so it lives at the bottom and
          colors itself by severity rather than firing a card-level
          alert. */}
      {promotion && (
        <CategoryCard category={promotion} flexBasis="100%" treatInfoAsOk />
      )}

      {lastDecision && (
        <div style={{ marginTop: 16 }}>
          <CategoryCard category={lastDecision} flexBasis="100%" treatInfoAsOk />
        </div>
      )}

      {disagreement && (
        <div style={{ marginTop: 16 }}>
          <DisagreementCard
            category={disagreement}
            onRowClick={setModalAlert}
          />
        </div>
      )}

      {kickoffQueue && (
        <div style={{ marginTop: 16 }}>
          <KickoffQueueCard
            category={kickoffQueue}
            onRowClick={setKickoffModalAlert}
          />
        </div>
      )}

      {modalAlert && (
        <DisagreementModal alert={modalAlert} onClose={() => setModalAlert(null)} />
      )}
      {kickoffModalAlert && (
        <KickoffQueueModal
          alert={kickoffModalAlert}
          onClose={() => setKickoffModalAlert(null)}
          onCancelled={() => {
            setKickoffModalAlert(null);
            qc.invalidateQueries({ queryKey: ["monitors"] });
          }}
        />
      )}
    </div>
  );
}
